/* eslint-disable camelcase */
import { Box3, Matrix4, Vector3 } from "three";
import * as turf from "@turf/turf";
import { IntersectType } from "./constant";
import {message} from "antd";
import {points} from "@turf/turf";
import { convertPointsArrToWkt } from "./gisUtils";

const getBox3FromVector3List = (points: Vector3[]) => {
    const xList = points.map((p) => p.x);
    const yList = points.map((p) => p.y);
    const zList = points.map((p) => p.z);

    const box3 = new Box3(
        new Vector3(Math.min(...xList), Math.min(...yList), Math.min(...zList)),
        new Vector3(Math.max(...xList), Math.max(...yList), Math.max(...zList))
    );

    return box3;
};

export const getTurfPolygonFromBox3 = (box3: Box3) =>
    turf.polygon([
        [
            [box3.min.x, box3.min.y],
            [box3.min.x, box3.max.y],
            [box3.max.x, box3.max.y],
            [box3.max.x, box3.min.y],
            [box3.min.x, box3.min.y]
        ]
    ]);

export const getTurfPolygonFromMeasurement = (measurement: any) => {
    const positions = measurement.points.map((p: any) => [p.position.x, p.position.y]);
    return turf.polygon([[...positions, positions[0]]]);
};

const intersects = (pointCloudOctree: any, node: any, measurement: any) => {
    const measurementTurfPolygon = getTurfPolygonFromMeasurement(measurement);

    const bbWorld = node.boundingBox.clone().applyMatrix4(pointCloudOctree.matrixWorld);
    const nodeTurfPolygon = getTurfPolygonFromBox3(bbWorld);

    return turf.intersect(measurementTurfPolygon, nodeTurfPolygon);
};

const getIntersectedNodes = (pointCloudOctree: any, measurement: any) => {
    const stack = [];
    const ret = [];

    const root = pointCloudOctree.pcoGeometry.root;

    stack.push(root);

    for (let i = 0; i < 8; i++) {
        const child = root.children[i];

        if (child && intersects(pointCloudOctree, child, measurement)) {
            stack.push(child);
        }
    }

    while (stack.length > 0) {
        const node: any = stack.pop();

        ret.push(node);

        for (let i = 0; i < 8; i++) {
            const child: any = node.children[i];

            if (child && intersects(pointCloudOctree, child, measurement)) {
                stack.push(child);
            }
        }
    }

    return ret;
};

const getPoints = (pointCloudOctree: any, node: any, measurement: any) => {
    const view = new Float32Array(node.geometry.attributes.position.array);
    const numPoints = node.numPoints;

    const pos = new Vector3();
    const nodeMatrix = new Matrix4().makeTranslation(
        node.boundingBox.min.x,
        node.boundingBox.min.y,
        node.boundingBox.min.z
    );

    const matrix = new Matrix4().multiplyMatrices(pointCloudOctree.matrixWorld, nodeMatrix);

    let accepted = new Uint32Array(numPoints);
    let acceptedPositions = new Float32Array(numPoints * 3);
    let numAccepted = 0;

    const points = new window.potree.Points();

    for (let i = 0; i < numPoints; i++) {
        pos.set(view[i * 3], view[i * 3 + 1], view[i * 3 + 2]);

        pos.applyMatrix4(matrix);

        if (turf.booleanContains(getTurfPolygonFromMeasurement(measurement), turf.point([pos.x, pos.y]))) {
            points.boundingBox.expandByPoint(pos);

            pos.sub(pointCloudOctree.position);

            accepted[numAccepted] = i;

            acceptedPositions[3 * numAccepted] = pos.x;
            acceptedPositions[3 * numAccepted + 1] = pos.y;
            acceptedPositions[3 * numAccepted + 2] = pos.z;

            numAccepted++;
        }
    }

    accepted = accepted.subarray(0, numAccepted);
    acceptedPositions = acceptedPositions.subarray(0, numAccepted * 3);

    // store position data
    points.data.position = acceptedPositions;

    const geometry = node.geometry;

    const relevantAttributes = Object.keys(geometry.attributes).filter((a) => !["position", "indices"].includes(a));

    relevantAttributes.forEach((attributeName) => {
        const attribute = geometry.attributes[attributeName];
        const numElements = attribute.array.length / numPoints;

        if (numElements !== Math.floor(numElements)) {
            throw Error("should not be reached!");
        }

        const Type = attribute.array.constructor;

        const filteredBuffer = new Type(numElements * accepted.length);

        const source = attribute.array;

        for (let i = 0; i < accepted.length; i++) {
            const index = accepted[i];

            const start = index * numElements;
            const end = start + numElements;
            const sub = source.subarray(start, end);

            filteredBuffer.set(sub, i * numElements);
        }

        points.data[attributeName] = filteredBuffer;
    });

    points.numPoints = accepted.length;

    return points;
};

const getPointsInPolygon = (viewer: any, measurement: any, qualityLevel: any) => {
    const vector3List: Vector3[] = measurement.points.map((p: any) => p.position);
    const bboxOfMeasure = getBox3FromVector3List(vector3List);

    const pointClouds = viewer.scene.pointclouds;

    const totalPoints = new window.potree.Points();

    pointClouds.forEach((pointCloudOctree: any) => {
        const pointCloudOctreeBoundingBox = pointCloudOctree.boundingBox
            .clone()
            .applyMatrix4(pointCloudOctree.matrixWorld);

        if (pointCloudOctreeBoundingBox.intersectsBox(bboxOfMeasure)) {
            // PointCloudOctreeGeometryNode array
            const nodes = getIntersectedNodes(pointCloudOctree, measurement);

            nodes.forEach((node: any) => {
                if (node.geometry === null) {
                    return;
                }

                if (qualityLevel !== -1 && node.level > qualityLevel) {
                    return;
                }

                const points: any = getPoints(pointCloudOctree, node, measurement);

                totalPoints.add(points);
            });
        }
    });

    if (pointClouds.length > 0) {
        const pcoGeometry = pointClouds[0].pcoGeometry;
        if (pcoGeometry.info.srs) {
            totalPoints.crs = pcoGeometry.info.srs.wkt;
        }
    }

    return totalPoints;
};


export const generatePolygonWKT = (aoi_data: any) => {

    let measurements = window.potreeViewer.scene.measurements;

    let measurement = measurements.find((m: any) => m.userData.name === aoi_data.name);

    if(!measurement) return ""

    let points = measurement.points.map((p: any) => [p.position.x, p.position.y, p.position.z]);

    let polygonWKT = convertPointsArrToWkt(points);
    return polygonWKT
};